import React, { useEffect, useState } from 'react';
import { ContextSelectorMenu, ContextSettingSelectorProps, useEditContextSettings } from '@ecosystem/newron-core';
import { actions, feedback, inputs } from '@ecosystem/newron-design-system';
import { Entity, Periodicity } from '@ecosystem/newron-core/types';
import { Mode } from './types';
import { SelectedMarkets } from './selected';
import { MarketsSelector } from './selector';
import { Market } from '../types';
import { NavMenuButton, NavMenuSeparator } from '../common';
import { RecentMarkets, updateRecentMarkets } from './recent';

const { Button } = actions;
const { DotsLoader } = feedback;
const { Checkbox, CheckboxState } = inputs;

function MarketContextSelector({ onChange, onClose }: ContextSettingSelectorProps) {
  const [mode, setMode] = useState<Mode>('byProduct');
  const [markets, setMarkets] = useState<Market[] | undefined>();
  const [cells, setCells] = useState<Entity[]>([]);
  const [indicateSubscriptions, setIndicateSubscriptions] = useState<boolean>(false);

  const { getContextSettings } = useEditContextSettings();

  useEffect(() => {
    const fetchSettingsAndUpdateState = async () => {
      const settings = await getContextSettings(['openMarkets', 'cell']);
      const markets = extractMarkets(settings['openMarkets']);
      updateMarketSubscriptions(markets);
      setMarkets(markets);
      setCells(settings['cell'].values);
    };

    fetchSettingsAndUpdateState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extractMarkets = (openMarketsSetting: any): Market[] => {
    return openMarketsSetting.definition.selection.options.filter(opt => typeof opt !== 'string') as Market[];
  };

  const updateMarketSubscriptions = (markets: Market[]) => {
    markets.forEach(market => {
      let subscriptions = 'M';
      if (market.periods.find(p => p.periodicity === Periodicity.Weekly)) {
        subscriptions += '/W';
      }
      market.subscriptions = subscriptions;
    });
  };

  const handleRemoveCells = (cellsToRemove: string[]) => {
    setCells(cells.filter(c => !cellsToRemove.includes(c.value)));
  };

  const handleAddCells = (cellsToAdd: Entity[]) => {
    setCells([...cells, ...cellsToAdd]);
  };

  const handleApply = () => {
    openMarkets(cells);
  };

  const handleRevisit = (cells: Entity[]) => {
    openMarkets(cells);
  };

  const openMarkets = (cells: Entity[]) => {
    if (!cells.length) {
      return;
    }
    updateRecentMarkets(cells);
    onChange({
      cell: cells,
    });
    onClose();
  };

  return (
    <ContextSelectorMenu>
      {!markets && (
        <div className="w-192 h-72">
          <DotsLoader stretch />
        </div>
      )}
      {markets && (
        <div className="relative">
          <div className="relative flex flex-row items-stretch" style={{ height: '460px' }}>
            <div className="flex-shrink-0 flex flex-column w-40 min-w-40 px-2 py-2 bg-light border-r border-light">
              <div className="flex-grow">
                <NavMenuButton
                  label="Recently used"
                  isActive={mode === 'byRecent'}
                  onClick={() => setMode('byRecent')}
                />
                <NavMenuSeparator />
                <NavMenuButton
                  label="By product"
                  isActive={mode === 'byProduct'}
                  onClick={() => setMode('byProduct')}
                />
                <NavMenuButton
                  label="By country"
                  isActive={mode === 'byCountry'}
                  onClick={() => setMode('byCountry')}
                />
              </div>
              {mode !== 'byRecent' && (
                /* eslint-disable jsx-a11y/label-has-associated-control */
                <label className="text-xs flex gap-1 cursor-pointer">
                  <Checkbox
                    onChange={state => setIndicateSubscriptions(state === CheckboxState.CHECKED)}
                    state={indicateSubscriptions ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                  />
                  Indicate (M)onthly or (W)eekly
                </label>
              )}
            </div>
            {mode === 'byRecent' && <RecentMarkets markets={markets} onSelect={handleRevisit} />}
            {mode !== 'byRecent' && (
              <MarketsSelector
                onAddCells={handleAddCells}
                mode={mode}
                cells={cells}
                markets={markets}
                indicateSubscriptions={indicateSubscriptions}
              />
            )}
            {mode !== 'byRecent' && (
              <SelectedMarkets
                onRemoveCells={handleRemoveCells}
                mode={mode}
                cells={cells}
                markets={markets}
                indicateSubscriptions={indicateSubscriptions}
              />
            )}
          </div>
          <div className="border-t border-default px-4 py-2 flex items-center justify-between">
            <Button mode="secondary" onClick={onClose}>
              Cancel
            </Button>
            {mode !== 'byRecent' && (
              <Button onClick={handleApply} disabled={cells.length === 0}>
                {cells.length > 0 && `View (${cells.length}) market(s)`}
                {cells.length === 0 && 'Select at least one market'}
              </Button>
            )}
          </div>
        </div>
      )}
    </ContextSelectorMenu>
  );
}

export { MarketContextSelector };
